const typing = new WeakMap<Element, number[]>()

/** Wrap every word in a mask span so CSS can slide words up on reveal. */
export function splitWords(el: HTMLElement, text: string): void {
  el.classList.add('split')
  const frag = document.createDocumentFragment()
  let index = 0
  for (const part of text.split(/(\s+)/)) {
    if (!part) continue
    if (/^\s+$/.test(part)) {
      frag.append(document.createTextNode(' '))
      continue
    }
    const mask = document.createElement('span')
    mask.className = 'w'
    const word = document.createElement('span')
    word.textContent = part
    word.style.setProperty('--i', String(index++))
    mask.append(word)
    frag.append(mask)
  }
  el.replaceChildren(frag)
}

export function cancelTyping(el: Element): void {
  typing.get(el)?.forEach((id) => {
    window.clearTimeout(id)
    window.clearInterval(id)
  })
  typing.delete(el)
}

/**
 * Builds the reserved typewriter markup: an invisible full copy of `text` holds the layout,
 * and a typed copy with a block caret sits on top. Both copies share font, width and
 * white-space, so the typed prefix wraps exactly where the final text will.
 */
function reserveTyping(el: HTMLElement, text: string): { typed: HTMLElement; caret: HTMLElement } {
  const box = document.createElement('span')
  box.className = 'tw'
  const ghost = document.createElement('span')
  ghost.className = 'tw__ghost'
  ghost.textContent = text
  const live = document.createElement('span')
  live.className = 'tw__live'
  live.setAttribute('aria-hidden', 'true')
  const typed = document.createElement('span')
  const caret = document.createElement('span')
  caret.className = 'tw__caret'
  caret.textContent = '█'
  live.append(typed, caret)
  box.append(ghost, live)
  el.replaceChildren(box)
  return { typed, caret }
}

/**
 * Cyber-mode typewriter, as on the current site: the reserved layout from `reserveTyping`
 * with the caret hidden until typing starts.
 */
export function typeInto(el: HTMLElement, text: string, opts: { delay?: number; speed?: number } = {}): void {
  cancelTyping(el)
  el.classList.remove('split')
  if (!text) {
    el.textContent = text
    return
  }
  const { typed, caret } = reserveTyping(el, text)
  caret.hidden = true

  const speed = opts.speed ?? (text.length > 60 ? 14 : 34)
  const ids: number[] = []
  typing.set(el, ids)
  ids.push(
    window.setTimeout(() => {
      caret.hidden = false
      let i = 0
      const id = window.setInterval(() => {
        typed.textContent = text.slice(0, ++i)
        if (i < text.length) return
        window.clearInterval(id)
        ids.push(window.setTimeout(() => caret.remove(), 800))
      }, speed)
      ids.push(id)
    }, opts.delay ?? 0),
  )
}

/**
 * Character-by-character typing with a block cursor (boot overlay and Cyber hint).
 * Reserves the final layout first, so a growing box never re-centres or re-flows.
 */
export function typewrite(el: HTMLElement, text: string, speed = 70): () => void {
  if (!text) {
    el.textContent = ''
    return () => {}
  }
  const { typed, caret } = reserveTyping(el, text)
  let i = 0
  const id = window.setInterval(() => {
    typed.textContent = text.slice(0, ++i)
    if (i >= text.length) {
      window.clearInterval(id)
      window.setTimeout(() => caret.remove(), 900)
    }
  }, speed)
  return () => window.clearInterval(id)
}
